import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getModules } from "../../../services/ModuleService";
import { getClassModules, setClassModules } from "../../../services/ClassService";

// Affectation de plusieurs modules à une classe. Seuls les modules de la
// spécialité de la classe sont proposés.
const ClassModulesModal = ({ classItem, onHide }) => {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!classItem) return;
    setLoading(true);
    setError("");
    setSaved(false);
    Promise.all([
      getModules({ page: 1, limit: 500, department_id: classItem.department_id, is_active: true }),
      getClassModules(classItem.id),
    ])
      .then(([modulesRes, assignedRes]) => {
        setModules(modulesRes.data?.data || []);
        setSelected(new Set((assignedRes.data?.data || []).map((m) => m.id)));
      })
      .catch((err) => setError(err?.response?.data?.message || t("pages.classes.modulesLoadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classItem]);

  const toggle = (id) => {
    setSaved(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await setClassModules(classItem.id, [...selected]);
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.message || t("pages.classes.modulesSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={!!classItem} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("pages.classes.modulesTitle", { name: classItem?.name || "" })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        {saved && <div className="alert alert-success">{t("pages.classes.modulesSaved")}</div>}
        {loading ? (
          <div className="text-center py-3"><div className="spinner-border text-primary" role="status" /></div>
        ) : modules.length === 0 ? (
          <p className="text-muted mb-0">{t("pages.classes.modulesNone")}</p>
        ) : (
          <>
            <p className="text-muted small">{t("pages.classes.modulesHelp")}</p>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {modules.map((m) => (
                <div className="form-check mb-2" key={m.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`class-module-${m.id}`}
                    checked={selected.has(m.id)}
                    onChange={() => toggle(m.id)}
                  />
                  <label className="form-check-label" htmlFor={`class-module-${m.id}`}>
                    <strong>{m.code}</strong> - {m.name}
                    <span className="text-muted"> ({m.credits ?? 0} {t("pages.modules.creditsShort")})</span>
                  </label>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onHide}>{t("common.crud.cancel")}</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || loading || modules.length === 0}>
          {saving ? t("pages.classes.modulesSaving") : t("pages.classes.modulesSave", { count: selected.size })}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClassModulesModal;
